import React, { createContext, useContext, useReducer, useEffect, useState, useRef, useMemo } from 'react';
import { trademarkService } from '../services/api/trademarkService';
import { useUser } from '@clerk/clerk-react';
import { useUserApplication } from './UserApplicationContext';

// Create context
const TrademarkContext = createContext();

// Initial state - integrated with application system
const initialState = {
  trademarks: [], 
  activeTrademark: null,
  userInfo: null,
  loading: false,
  error: null,
  clerkUserId: null,
  visionAnalysisStatus: null, // 'pending', 'complete', 'error'
  visionAnalysisError: null
};

// Reducer function - simplified to focus only on trademark management
const trademarkReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    case 'SET_USER_INFO':
      return {
        ...state,
        userInfo: action.payload,
        loading: false
      };
    case 'UPDATE_USER_INFO':
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          ...action.payload
        },
        loading: false
      };
    case 'SET_TRADEMARKS':
      return {
        ...state,
        trademarks: action.payload,
        // Automatically set the first trademark as active if there isn't already an active one
        activeTrademark: state.activeTrademark || (action.payload && action.payload.length > 0 ? action.payload[0] : null),
        loading: false
      };
    case 'ADD_TRADEMARK':
      // Ensure we don't add duplicates
      const isDuplicate = state.trademarks.some(
        tm => tm.id === action.payload.id
      );
      
      if (isDuplicate) {
        return state;
      }
      
      // Add the new trademark to the array
      return {
        ...state,
        trademarks: [...state.trademarks, action.payload],
        loading: false
      };
    case 'SET_ACTIVE_TRADEMARK':
      return {
        ...state,
        activeTrademark: action.payload,
        loading: false
      };
    case 'SET_CLERK_USER_ID':
      return {
        ...state,
        clerkUserId: action.payload
      };
    case 'SET_VISION_ANALYSIS_STATUS':
      return {
        ...state,
        visionAnalysisStatus: action.payload.status,
        visionAnalysisError: action.payload.error
      };
    case 'UPDATE_TRADEMARK':
      return {
        ...state,
        trademarks: state.trademarks.map(trademark => trademark.id === action.payload.id ? action.payload : trademark),
        activeTrademark: state.activeTrademark?.id === action.payload.id ? action.payload : state.activeTrademark,
        loading: false
      };
    case 'RESET_STATE':
      return {
        ...initialState
      };
    default:
      return state;
  }
};

// Context provider
export const TrademarkProvider = ({ children }) => {
  const [state, dispatch] = useReducer(trademarkReducer, initialState);
  // Get current application ID and thread ID from user application context
  const { currentApplicationId, currentThreadId } = useUserApplication();
  const { user } = useUser();
  
  // 🔐 CANONICAL AUTH GUARD: Load trademarks with proper auth guards
  useEffect(() => {
    // Multi-stage auth readiness check
    const authReady = user?.id && user.isLoading !== true; // Fix: Handle undefined Clerk properties
    const appReady = currentApplicationId;
    
    console.log('🔍 TrademarkContext useEffect triggered (CANONICAL PATTERN):', {
      'user?.id': user?.id,
      'user.isLoading': user?.isLoading,
      'user.isSignedIn': user?.isSignedIn,
      'authReady': authReady,
      'currentApplicationId': currentApplicationId,
      'appReady': appReady,
      'readyToLoad': authReady && appReady
    });
    
    if (authReady && appReady) {
      console.log('✅ TrademarkContext: Auth and app ready, loading trademarks for application:', currentApplicationId);
      loadTrademarksForApplication(currentApplicationId);
    } else if (!currentApplicationId) {
      console.log('❌ TrademarkContext: No currentApplicationId, clearing data');
      dispatch({ type: 'SET_TRADEMARKS', payload: [] });
      dispatch({ type: 'SET_ACTIVE_TRADEMARK', payload: null });
    } else {
      console.log('🔄 TrademarkContext: Waiting for auth or app context to be ready...');
    }
  }, [user?.id, user?.isLoading, user?.isSignedIn, currentApplicationId]);

  // Separate useEffect for Vision API completion event handling
  useEffect(() => {
    const handleTrademarkVisionCompleted = async (event) => {
      const { trademarkId } = event.detail;
      console.log('🔄 TrademarkContext: Vision API completed for trademark:', trademarkId);
      
      // Reload the specific trademark to get updated textDescription
      try {
        const updatedTrademark = await trademarkService.getTrademark(trademarkId);
        console.log('🔄 Reloaded trademark after vision processing - FULL OBJECT:', JSON.stringify(updatedTrademark, null, 2));
        console.log('🔄 textDescription specifically:', updatedTrademark.textDescription);
        console.log('🔄 Current activeTrademark before update:', state.activeTrademark);
        
        dispatch({ type: 'UPDATE_TRADEMARK', payload: updatedTrademark });
        
        // If this is the active trademark, update it too
        if (state.activeTrademark?.id === trademarkId) {
          console.log('🔄 Updating activeTrademark because IDs match:', trademarkId);
          dispatch({ type: 'SET_ACTIVE_TRADEMARK', payload: updatedTrademark });
        } else {
          console.log('🔄 Not updating activeTrademark - IDs do not match:', {
            activeId: state.activeTrademark?.id,
            updatedId: trademarkId
          });
        }
        
        console.log('🔄 activeTrademark after update should have textDescription:', updatedTrademark.textDescription);
      } catch (error) {
        console.error('Error reloading trademark after vision processing:', error);
      }
    };
    
    window.addEventListener('trademark_vision_completed', handleTrademarkVisionCompleted);
    
    return () => {
      window.removeEventListener('trademark_vision_completed', handleTrademarkVisionCompleted);
    };
  }, [user?.id]); // Only depend on user?.id, not currentApplicationId

  // Load trademarks for a specific application - EXACT SAME PATTERN AS BusinessContext
  const loadTrademarksForApplication = async (applicationId) => {
    try {
      if (!applicationId) {
        console.error('loadTrademarksForApplication called with empty applicationId!');
        return;
      }
      
      console.log('🔍 TrademarkContext: loadTrademarksForApplication called with:', applicationId); // This will show the full call stack
      
      dispatch({ type: 'SET_LOADING', payload: true });
      const trademarkData = await trademarkService.getTrademarksByApplication(applicationId);
      
      dispatch({ type: 'SET_TRADEMARKS', payload: trademarkData });
      
      // Set the first trademark as active if available
      if (trademarkData && trademarkData.length > 0) {
        dispatch({ type: 'SET_ACTIVE_TRADEMARK', payload: trademarkData[0] });
      } else {
        dispatch({ type: 'SET_ACTIVE_TRADEMARK', payload: null });
      }
      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      console.error('TrademarkContext: Error loading trademarks for application:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Get a specific trademark by ID
  const getTrademark = async (id) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const trademark = await trademarkService.getTrademark(id);

      dispatch({ type: 'SET_ACTIVE_TRADEMARK', payload: trademark });
      return trademark;
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error.message || 'Error fetching trademark' 
      });
      // Don't throw error - let UI continue functioning
      return null;
    }
  };

  // Upload a new trademark (image or name)
  const uploadTrademark = async (data) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      // Ensure we have a file or name - should never happen due to UI validation
      if (!data.file && !data.name) {
        throw new Error('No file or trademark name provided');
      }

      // Add application ID to the trademark data
      const applicationId = currentApplicationId || sessionStorage.getItem('currentApplicationId');
      const trademarkData = {
        ...data,
        applicationId
      };

      // Create the trademark
      const trademark = await trademarkService.createTrademark(trademarkData);

      // If we have a file, upload it
      if (data.file) {
        await trademarkService.uploadImage(trademark.id, data.file);

        // Trigger Vision API processing in the background
        dispatch({ 
          type: 'UPDATE_USER_INFO', 
          payload: { visionAnalysisStatus: 'pending', visionAnalysisError: null } 
        });

        // Process with Vision API in the background
        processTrademarkWithVisionAPI(trademark.id);
      }

      dispatch({ type: 'ADD_TRADEMARK', payload: trademark });
      dispatch({ type: 'SET_ACTIVE_TRADEMARK', payload: trademark });
      return trademark;
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error.message || 'Error uploading trademark' 
      });

      // Create a temporary local trademark object with minimal data
      // This allows the UI flow to continue without a backend
      const tempTrademark = {
        id: 'temp-' + Date.now(),
        name: data.name || 'Untitled Trademark',
        createdAt: new Date().toISOString(),
        status: 'draft'
      };

      dispatch({ type: 'ADD_TRADEMARK', payload: tempTrademark });
      dispatch({ type: 'SET_ACTIVE_TRADEMARK', payload: tempTrademark });
      return tempTrademark;
    }
  };

  // Update user info
  const updateUserInfo = (info) => {
    dispatch({ type: 'UPDATE_USER_INFO', payload: info });
    return info;
  };

  // Reset state (e.g., when starting a new trademark)
  const resetState = () => {
    dispatch({ type: 'RESET_STATE' });
  };

  // Update Clerk User ID
  const setClerkUserId = (userId) => {
    dispatch({ type: 'SET_CLERK_USER_ID', payload: userId });
    // No need to transfer session data since we're using authentication-only approach
    return userId;
  };

  // Process trademark with Vision API
  const processTrademarkWithVisionAPI = async (trademarkId) => {
    try {
      await trademarkService.processWithVisionAPI(trademarkId);

      // Update the active trademark if it matches the processed trademark
      if (state.activeTrademark && state.activeTrademark.id === trademarkId) {
        const updatedTrademark = await trademarkService.getTrademark(trademarkId);
        dispatch({ type: 'SET_ACTIVE_TRADEMARK', payload: updatedTrademark });
      }

      dispatch({
        type: 'SET_VISION_ANALYSIS_STATUS', 
        payload: { status: 'complete', error: null }
      });
    } catch (error) {
      dispatch({
        type: 'SET_VISION_ANALYSIS_STATUS', 
        payload: { status: 'error', error: error.message || 'Error analyzing trademark image' }
      });
    }
  };

  const value = useMemo(() => ({
    state, 
    dispatch,
    loadTrademarksForApplication,
    getTrademark,
    uploadTrademark,
    updateUserInfo,
    resetState,
    setClerkUserId,
    processTrademarkWithVisionAPI
  }), [state, dispatch]);

  return (
    <TrademarkContext.Provider value={value}>
      {children}
    </TrademarkContext.Provider>
  );
};

// Custom hook to use the trademark context
export const useTrademark = () => {
  const context = useContext(TrademarkContext);
  if (!context) {
    throw new Error('useTrademark must be used within a TrademarkProvider');
  }
  return context;
};

// Create a separate component for Clerk integration that will be used inside the app
// after the ClerkProvider is initialized
export const ClerkUserSync = () => {
  const { userId, isSignedIn } = useUser();
  const [hasInitialized, setHasInitialized] = useState(false);
  const previousUserIdRef = useRef(null);

  // Extract only what we need from the context to avoid dependency issues
  const { setClerkUserId } = useTrademark();

  // Effect to handle Clerk authentication state changes
  useEffect(() => {
    const currentUserId = userId;
    const prevUserId = previousUserIdRef.current;

    // Skip if the user ID hasn't changed
    if (currentUserId === prevUserId) {
      return;
    }

    // Update ref to current userId for future comparisons
    previousUserIdRef.current = currentUserId;

    if (isSignedIn && currentUserId) {
      // Set the user ID in context
      setClerkUserId(currentUserId);
    } else if (!isSignedIn && prevUserId !== null) {
      // User logged out
      setClerkUserId(null);
    }
  }, [userId, isSignedIn, setClerkUserId]);

  return null;
};
